import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { NurseryOrderLine, NurseryOrderLineAllocation } from '../../types/nursery';
import PbrMark from '../cultivar/PbrMark';
import PbrNotice from '../cultivar/PbrNotice';
import { isPbrProtected } from '../../constants/pbr';
import { TagSwatch } from './TagSwatch';

export interface OrderLineTableProps {
  lines: NurseryOrderLine[];
  onAllocate?: (lineId: number) => Promise<void>;
  /** Given before picking starts: each allocation can be released back to free stock. */
  onRelease?: (allocation: NurseryOrderLineAllocation, line: NurseryOrderLine) => void;
  /** The allocation being released, so its button can say so. */
  releasingId?: number | null;
}

/**
 * What stock a line sold under an umbrella product drew on — or, before
 * anything is allocated, which stock item it will be filled from first.
 */
function productFill(line: NurseryOrderLine, t: TFunction<'nursery', 'OrderLineTable'>): string {
  const filled = line.filled_from ?? [];
  if (filled.length > 0) {
    return filled
      .map((f) => `${[f.cultivar_name, f.rootstock_name].filter(Boolean).join(' / ')} × ${f.quantity}`)
      .join(', ');
  }
  return t('firstChoice', { name: [line.cultivar_name, line.rootstock_name].filter(Boolean).join(' / ') });
}

export function OrderLineTable({ lines, onAllocate, onRelease, releasingId }: OrderLineTableProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'OrderLineTable' });
  const showAction = !!onAllocate || !!onRelease;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200">
            <th className="text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide py-2 pr-3">{t('item')}</th>
            <th className="text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide py-2 px-3">{t('ordered')}</th>
            <th className="text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide py-2 px-3">{t('allocated')}</th>
            <th className="text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide py-2 px-3">{t('unitPrice')}</th>
            <th className="text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide py-2 pl-3">{t('total')}</th>
            {showAction && <th className="py-2 pl-3" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {lines.map(line => {
            // The name, not the code: codes are not unique ("20CM" is both a
            // bareroot and a pot), and the two do not allocate from the same lots.
            const itemLabel = [line.cultivar_name, line.rootstock_name, line.tree_type_name || line.tree_type_code]
              .filter(Boolean).join(' / ');
            const allocations = line.allocations ?? [];
            return (
              <Fragment key={line.id}>
              <tr>
                <td className="py-2 pr-3 text-neutral-800">
                  {line.product_name ? (
                    <>
                      <span className="inline-flex items-center gap-1.5">
                        <TagSwatch hex={line.tag_colour_hex} name={line.tag_colour_name} />
                        {line.product_name}
                      </span>
                      {/* Part of what was bought: the same name comes in other tree types. */}
                      {(line.tree_type_name || line.tree_type_code) && (
                        <span className="block text-xs text-neutral-600">{line.tree_type_name || line.tree_type_code}</span>
                      )}
                      <span className="block text-xs text-neutral-500">{productFill(line, t)}</span>
                    </>
                  ) : (
                    <>
                      {itemLabel || t('itemNumber', { id: line.stock_item_id })}
                      <PbrMark status={line.cultivar_protection_status} />
                    </>
                  )}
                </td>
                <td className="py-2 px-3 text-right text-neutral-700">{line.quantity_ordered}</td>
                <td className="py-2 px-3 text-right text-neutral-700">{line.quantity_allocated}</td>
                <td className="py-2 px-3 text-right text-neutral-500">
                  {line.unit_price != null ? `$${Number(line.unit_price).toFixed(2)}` : '—'}
                </td>
                <td className="py-2 pl-3 text-right text-neutral-700">
                  {line.line_total != null ? `$${Number(line.line_total).toFixed(2)}` : '—'}
                </td>
                {showAction && (
                  <td className="py-2 pl-3">
                    {onAllocate && line.quantity_allocated < line.quantity_ordered && (
                      <button
                        onClick={() => onAllocate(line.id)}
                        className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                      >
                        {t('allocate')}
                      </button>
                    )}
                  </td>
                )}
              </tr>
              {allocations.length > 0 && (
                <tr className="!border-t-0">
                  <td colSpan={showAction ? 6 : 5} className="pb-2 pt-0">
                    <ul className="space-y-1 pl-3 border-l-2 border-indigo-100">
                      {allocations.map((a) => (
                        <li key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-neutral-600">
                          <span className="min-w-0 break-words">
                            <Trans
                              t={t}
                              i18nKey="fromLot"
                              values={{ quantity: a.quantity, lot: a.lot_code || `#${a.lot_id}` }}
                              components={{
                                qty: <span className="font-medium text-neutral-800" />,
                                link: <Link to={`/nursery/lots/${a.lot_id}`} className="text-primary-700 hover:underline" />,
                              }}
                            />
                            {line.product_name && ` · ${[a.cultivar_name, a.rootstock_name].filter(Boolean).join(' / ')}`}
                            {a.location_name
                              ? ` · ${a.location_name}`
                              : a.status === 'allocated' && <span className="text-neutral-400">{t('noPlace')}</span>}
                            {a.status !== 'allocated' && ` · ${a.status}`}
                          </span>
                          {onRelease && a.status === 'allocated' && (
                            <button
                              type="button"
                              onClick={() => onRelease(a, line)}
                              disabled={releasingId != null}
                              className="font-medium text-red-600 hover:text-red-800 disabled:text-neutral-400"
                            >
                              {releasingId === a.id ? t('releasing') : t('release')}
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {lines.some((l) => !l.product_name && isPbrProtected(l.cultivar_protection_status)) && (
        <PbrNotice className="mt-3" />
      )}
    </div>
  );
}
